/**
 * Crash-recovery snapshot for the in-flight dictation transcript.
 *
 * Every `end_of_turn` we mirror the accumulated transcript to
 * localStorage. If the browser crashes, the tab is killed, or the
 * laptop goes flat mid-dictation, the doctor (or a support engineer)
 * can recover the text from this entry on the next load — they
 * otherwise lose up to an hour of clinical notes that exist nowhere
 * else.
 *
 * Snapshots are cleared on graceful session end (user clicks stop,
 * confirms silence-stop, or AssemblyAI sends Termination). They are
 * deliberately NOT cleared on the unrecoverable-error path, since
 * that is the exact scenario we want recoverable.
 *
 * Storage is best-effort: any failure (Safari private mode quota,
 * disabled localStorage, parse error from a stale schema) is
 * swallowed. We never block the dictation flow on storage I/O.
 */

const STORAGE_KEY = "medirect.transcriber.recovery";

export type TranscriptSnapshot = {
  /** window.location.pathname at the time of save — lets the recoverer
   *  decide whether the snapshot belongs to the current case. */
  pathname: string;
  /** Wall-clock time of the snapshot in ms since epoch. */
  savedAt: number;
  /** Full accumulated transcript, no trailing newline normalisation. */
  transcript: string;
};

export function saveTranscriptSnapshot(
  transcript: string,
  pathname: string = typeof window === "undefined" ? "" : window.location.pathname
): void {
  if (typeof window === "undefined") return;
  if (!transcript) return;
  const record: TranscriptSnapshot = {
    pathname,
    savedAt: Date.now(),
    transcript,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* QuotaExceeded, disabled storage, private browsing — best-effort. */
  }
}

export function clearTranscriptSnapshot(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function readTranscriptSnapshot(): TranscriptSnapshot | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as TranscriptSnapshot).pathname !== "string" ||
      typeof (parsed as TranscriptSnapshot).savedAt !== "number" ||
      typeof (parsed as TranscriptSnapshot).transcript !== "string"
    ) {
      return null;
    }
    return parsed as TranscriptSnapshot;
  } catch {
    return null;
  }
}
